import React, { useState, useEffect, useMemo } from 'react';
import { db, auth } from './firebase';
import {
  collection, query, onSnapshot, doc, addDoc, updateDoc, serverTimestamp
} from 'firebase/firestore';
import { useRole } from './RoleContext';
import { Wallet, Plus, X, User } from 'lucide-react';
import { deleteToRecycleBin } from './recycleBinUtil';

const formatTk = (num) => new Intl.NumberFormat('en-IN').format(Math.round(num || 0));

const getCurrentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const getTodayString = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  const d = dateStr?.toDate ? dateStr.toDate() : new Date(dateStr);
  if (isNaN(d.getTime())) return 'N/A';
  return `${String(d.getDate()).padStart(2, '0')}-${d.toLocaleString('en-US', { month: 'short' })}-${d.getFullYear()}`;
};

// Shrinks the picked image client-side into a small base64 data URL --
// same free (no Firebase Storage) pattern already used for profile photos in Settings.js.
const resizeImage = (file, maxSize = 150) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxSize / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

const emptyEmployeeForm = {
  name: '', phone: '', position: '', department: '', employmentType: 'Full-Time', gender: 'Male',
  monthlySalary: '', joiningDate: '', photoUrl: '',
  bkashNumber: '', bankAccountNumber: '', bankAccountName: '', bankBranchName: '', bankName: '', bankRoutingNumber: ''
};

const Payroll = ({ embedded = false }) => {
  const { isFullAccess: isAdmin } = useRole();

  const [employees, setEmployees] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [month, setMonth] = useState(getCurrentMonth());
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'terminated'

  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState(emptyEmployeeForm);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [terminatingEmployee, setTerminatingEmployee] = useState(null);
  const [terminationDate, setTerminationDate] = useState(getTodayString());
  const [terminationReason, setTerminationReason] = useState('');

  const [reactivatingEmployee, setReactivatingEmployee] = useState(null);
  const [reactivationReason, setReactivationReason] = useState('');

  const [actionEmployee, setActionEmployee] = useState(null); // employee row currently doing pay/advance/repay
  const [actionType, setActionType] = useState(null); // 'salary' | 'advance' | 'repay'
  const [actionAmount, setActionAmount] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [actionMonth, setActionMonth] = useState(getCurrentMonth());
  const [actionPaymentMethod, setActionPaymentMethod] = useState('Cash');

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'employees')), (snap) => {
      setEmployees(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  // The New/Edit Employee form should only close via the X button or Esc --
  // Sohan wants outside clicks to be a no-op so an accidental click doesn't lose the form.
  useEffect(() => {
    if (!showEmployeeForm) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') setShowEmployeeForm(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showEmployeeForm]);

  useEffect(() => {
    // Full transaction history is listened to live -- this shop has few staff,
    // so pulling the whole collection is simpler and fast enough (no where filter needed, unlike Due.js).
    const unsub = onSnapshot(query(collection(db, 'payrollTransactions')), (snap) => {
      setTransactions(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const activeEmployees = useMemo(
    () => employees.filter((e) => e.active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [employees]
  );
  const terminatedEmployees = useMemo(
    () => employees.filter((e) => e.active === false).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [employees]
  );

  const summaryByEmployee = useMemo(() => {
    const map = {};
    employees.forEach((e) => {
      const empTxns = transactions.filter((t) => t.employeeId === e.id);
      const salaryPaidThisMonth = empTxns
        .filter((t) => t.type === 'salary' && t.month === month)
        .reduce((s, t) => s + Number(t.amount || 0), 0);
      // A 'advance' transaction with a NEGATIVE amount means an advance being repaid/settled --
      // summing all 'advance' amounts per employee gives the outstanding balance directly.
      const advanceBalance = empTxns
        .filter((t) => t.type === 'advance')
        .reduce((s, t) => s + Number(t.amount || 0), 0);
      const salaryDue = Math.max(0, Number(e.monthlySalary || 0) - salaryPaidThisMonth);
      map[e.id] = { salaryPaidThisMonth, advanceBalance, salaryDue };
    });
    return map;
  }, [employees, transactions, month]);

  const totals = useMemo(() => {
    return activeEmployees.reduce((acc, e) => {
      const s = summaryByEmployee[e.id] || {};
      acc.salaryDue += s.salaryDue || 0;
      acc.advanceOutstanding += s.advanceBalance || 0;
      acc.salaryPaidThisMonth += s.salaryPaidThisMonth || 0;
      return acc;
    }, { salaryDue: 0, advanceOutstanding: 0, salaryPaidThisMonth: 0 });
  }, [activeEmployees, summaryByEmployee]);

  const openAddEmployee = () => {
    setEditingEmployee(null);
    setEmployeeForm(emptyEmployeeForm);
    setShowEmployeeForm(true);
  };

  const openEditEmployee = (emp) => {
    setEditingEmployee(emp);
    setEmployeeForm({
      name: emp.name || '', phone: emp.phone || '', position: emp.position || '',
      department: emp.department || '', employmentType: emp.employmentType || 'Full-Time', gender: emp.gender || 'Male',
      monthlySalary: emp.monthlySalary || '', joiningDate: emp.joiningDate || '', photoUrl: emp.photoUrl || '',
      bkashNumber: emp.bkashNumber || '',
      bankAccountNumber: emp.bankAccountNumber || '', bankAccountName: emp.bankAccountName || '',
      bankBranchName: emp.bankBranchName || '', bankName: emp.bankName || '', bankRoutingNumber: emp.bankRoutingNumber || ''
    });
    setShowEmployeeForm(true);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) return alert('Please choose an image file.');
    setUploadingPhoto(true);
    try {
      const dataUrl = await resizeImage(file);
      setEmployeeForm((f) => ({ ...f, photoUrl: dataUrl }));
    } catch (err) {
      alert("Couldn't read that image: " + err.message);
    }
    setUploadingPhoto(false);
  };

  const saveEmployee = async () => {
    if (!isAdmin) return;
    const name = employeeForm.name.trim();
    if (!name) return alert('Please enter a name.');
    const payload = {
      name,
      phone: employeeForm.phone.trim(),
      position: employeeForm.position.trim(),
      department: employeeForm.department.trim(),
      employmentType: employeeForm.employmentType || 'Full-Time',
      gender: employeeForm.gender || 'Male',
      monthlySalary: Number(employeeForm.monthlySalary) || 0,
      joiningDate: employeeForm.joiningDate || '',
      photoUrl: employeeForm.photoUrl || '',
      bkashNumber: employeeForm.bkashNumber.trim(),
      bankAccountNumber: employeeForm.bankAccountNumber.trim(),
      bankAccountName: employeeForm.bankAccountName.trim(),
      bankBranchName: employeeForm.bankBranchName.trim(),
      bankName: employeeForm.bankName.trim(),
      bankRoutingNumber: employeeForm.bankRoutingNumber.trim()
    };
    try {
      if (editingEmployee) {
        await updateDoc(doc(db, 'employees', editingEmployee.id), payload);
      } else {
        await addDoc(collection(db, 'employees'), { ...payload, active: true, createdAt: serverTimestamp() });
      }
      setShowEmployeeForm(false);
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const openTerminate = (emp) => {
    setTerminatingEmployee(emp);
    setTerminationDate(getTodayString());
    setTerminationReason('');
  };

  const confirmTerminate = async () => {
    if (!isAdmin || !terminatingEmployee || !terminationReason.trim()) return;
    await updateDoc(doc(db, 'employees', terminatingEmployee.id), { active: false, terminationDate, terminationReason: terminationReason.trim() });
    setTerminatingEmployee(null);
  };

  const openReactivate = (emp) => {
    setReactivatingEmployee(emp);
    setReactivationReason('');
  };

  const confirmReactivate = async () => {
    if (!isAdmin || !reactivatingEmployee || !reactivationReason.trim()) return;
    await updateDoc(doc(db, 'employees', reactivatingEmployee.id), { active: true, terminationDate: null, reactivationReason: reactivationReason.trim() });
    setReactivatingEmployee(null);
  };

  const removeEmployee = async (emp) => {
    if (!isAdmin) return;
    if (!window.confirm(`Delete ${emp.name}? Their past payroll records stay, and the employee can be restored from the Recycle Bin.`)) return;
    await deleteToRecycleBin({ collectionName: 'employees', id: emp.id, data: emp, label: 'Employee' });
  };

  const openAction = (emp, type) => {
    setActionEmployee(emp);
    setActionType(type);
    setActionAmount('');
    setActionNote('');
    setActionMonth(month);
    setActionPaymentMethod('Cash');
  };

  const closeAction = () => {
    setActionEmployee(null);
    setActionType(null);
  };

  const submitAction = async () => {
    if (!isAdmin || !actionEmployee) return;
    const amt = Number(actionAmount);
    if (!amt || amt <= 0) return alert('Please enter a valid amount.');

    // A 2nd (or later) salary payment to the same employee in the same month is
    // unusual -- could be a legitimate partial/installment payment, or an accidental
    // double-entry. Require a note explaining it before it's accepted, same
    // "must justify" pattern as Terminate/Reactivate.
    if (actionType === 'salary') {
      const alreadyPaidThisMonth = transactions.some((t) => t.employeeId === actionEmployee.id && t.type === 'salary' && t.month === actionMonth);
      if (alreadyPaidThisMonth && !actionNote.trim()) {
        return alert(`${actionEmployee.name} already has a salary payment recorded for ${actionMonth}. Please add a note explaining this additional payment (e.g. "2nd installment") before confirming.`);
      }
    }

    try {
      const signedAmount = actionType === 'repay' ? -amt : amt;
      const paymentDetails = actionPaymentMethod === 'Bank'
        ? {
            accountNumber: actionEmployee.bankAccountNumber || '', accountName: actionEmployee.bankAccountName || '',
            branchName: actionEmployee.bankBranchName || '', bankName: actionEmployee.bankName || '', routingNumber: actionEmployee.bankRoutingNumber || ''
          }
        : actionPaymentMethod === 'bKash'
          ? { bkashNumber: actionEmployee.bkashNumber || '' }
          : null;
      await addDoc(collection(db, 'payrollTransactions'), {
        employeeId: actionEmployee.id, employeeName: actionEmployee.name,
        type: actionType === 'salary' ? 'salary' : 'advance',
        amount: signedAmount, month: actionMonth, note: actionNote.trim(),
        paymentMethod: actionPaymentMethod, paymentDetails,
        createdAt: serverTimestamp(), createdBy: auth.currentUser?.email || ''
      });

      // Salary paid out is a real business expense -- record it in Accounts & Ledger
      // too, so the ledger's daily/monthly expense totals actually include payroll
      // (previously Payroll and Accounts were two disconnected systems).
      if (actionType === 'salary') {
        await addDoc(collection(db, 'transactions'), {
          type: 'expense',
          category: 'Salary Expense',
          description: `${actionEmployee.name} — Salary (${actionMonth})${actionNote.trim() ? ` — ${actionNote.trim()}` : ''}`,
          amount: amt,
          method: actionPaymentMethod,
          date: getTodayString(),
          addedBy: auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Staff',
          createdAt: serverTimestamp()
        });
      }

      closeAction();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const historyForEmployee = (empId) => transactions
    .filter((t) => t.employeeId === empId)
    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0))
    .slice(0, 5);

  const actionTitle = { salary: 'Pay Salary', advance: 'Give Advance', repay: 'Repay Advance' }[actionType];

  const Avatar = ({ url }) => url ? (
    <img src={url} alt="" style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'cover' }} />
  ) : (
    <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#f4f7fe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4a00e0' }}>
      <User size={16} />
    </div>
  );

  const boxContent = (
    <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
          <h2 style={{ margin: 0, color: '#333' }}><Wallet size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />Payroll</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input type="month" className="input-field" value={month} onChange={(e) => setMonth(e.target.value)} style={{ width: '160px', marginBottom: 0 }} />
            {isAdmin && (
              <button onClick={openAddEmployee} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', borderRadius: '18px', border: 'none', background: 'linear-gradient(45deg, #4a00e0, #8e2de2)', color: '#fff', fontWeight: 'bold', cursor: 'pointer', flexShrink: 0 }}>
                <Plus size={16} /> New Employee
              </button>
            )}
          </div>
        </div>

        {!isAdmin && (
          <div style={{ background: '#fff3cd', color: '#8a6d3b', padding: '10px 15px', borderRadius: '10px', marginBottom: '20px', fontSize: '13px' }}>
            Only the Admin account can add/edit employees or record salary/advance payments. You have view-only access.
          </div>
        )}

        <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginBottom: '25px' }}>
          <div className="hover-lift" style={{ background: '#fff', padding: '20px', borderRadius: '20px', borderLeft: '6px solid #4a00e0', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', minWidth: '180px', flex: 1 }}>
            <p style={{ margin: 0, color: '#666', fontSize: '13px', fontWeight: 'bold' }}>Total Employees</p>
            <h2 style={{ margin: 0, color: '#4a00e0', fontSize: '22px' }}>{activeEmployees.length}</h2>
          </div>
          {[
            { label: 'Salary Paid This Month', value: totals.salaryPaidThisMonth, color: '#10b981' },
            { label: 'Salary Due This Month (All Employees)', value: totals.salaryDue, color: '#f59e0b' },
            { label: 'Total Advance Outstanding', value: totals.advanceOutstanding, color: '#e74c3c' }
          ].map((t) => (
            <div className="hover-lift" key={t.label} style={{ background: '#fff', padding: '20px', borderRadius: '20px', borderLeft: `6px solid ${t.color}`, boxShadow: '0 4px 15px rgba(0,0,0,0.05)', minWidth: '240px', flex: 1 }}>
              <p style={{ margin: 0, color: '#666', fontSize: '13px', fontWeight: 'bold' }}>{t.label}</p>
              <h2 style={{ margin: 0, color: t.color, fontSize: '22px' }}>৳ {formatTk(t.value)}</h2>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '18px' }}>
          <button
            onClick={() => setActiveTab('active')}
            style={{ padding: '10px 18px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', border: `1px solid ${activeTab === 'active' ? '#4a00e0' : '#ddd'}`, background: activeTab === 'active' ? '#4a00e0' : '#fff', color: activeTab === 'active' ? '#fff' : '#333' }}
          >
            Active Employees ({activeEmployees.length})
          </button>
          <button
            onClick={() => setActiveTab('terminated')}
            style={{ padding: '10px 18px', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', border: `1px solid ${activeTab === 'terminated' ? '#4a00e0' : '#ddd'}`, background: activeTab === 'terminated' ? '#4a00e0' : '#fff', color: activeTab === 'terminated' ? '#fff' : '#333' }}
          >
            Terminated Employees ({terminatedEmployees.length})
          </button>
        </div>

        {activeTab === 'active' && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
                <th style={{ padding: '15px' }}>Name</th>
                <th>Position</th>
                <th>Monthly Salary</th>
                <th>Paid This Month</th>
                <th>Due This Month</th>
                <th>Advance Balance</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {activeEmployees.map((emp) => {
                const s = summaryByEmployee[emp.id] || {};
                return (
                  <tr key={emp.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                    <td style={{ padding: '15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Avatar url={emp.photoUrl} />
                        <div>
                          <b>{emp.name}</b>
                          <div style={{ fontSize: '11px', color: '#999' }}>{emp.phone}</div>
                        </div>
                      </div>
                    </td>
                    <td>{emp.position || '-'}</td>
                    <td>৳ {formatTk(emp.monthlySalary)}</td>
                    <td style={{ color: '#10b981', fontWeight: 'bold' }}>৳ {formatTk(s.salaryPaidThisMonth)}</td>
                    <td>
                      <span style={{ padding: '4px 10px', background: s.salaryDue > 0 ? '#fce8e6' : '#eafff3', color: s.salaryDue > 0 ? '#c5221f' : '#218c61', borderRadius: '8px', fontWeight: 'bold', fontSize: '12px' }}>
                        ৳ {formatTk(s.salaryDue)}
                      </span>
                    </td>
                    <td style={{ color: s.advanceBalance > 0 ? '#e74c3c' : '#999', fontWeight: 'bold' }}>৳ {formatTk(s.advanceBalance)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {isAdmin && <button onClick={() => openAction(emp, 'salary')} style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', border: 'none', background: '#4a00e0', color: '#fff', cursor: 'pointer' }}>Pay Salary</button>}
                        {isAdmin && <button onClick={() => openAction(emp, 'advance')} style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', border: 'none', background: '#f59e0b', color: '#fff', cursor: 'pointer' }}>Give Advance</button>}
                        {isAdmin && s.advanceBalance > 0 && <button onClick={() => openAction(emp, 'repay')} style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', border: 'none', background: '#10b981', color: '#fff', cursor: 'pointer' }}>Repay Advance</button>}
                        {isAdmin && <button onClick={() => openEditEmployee(emp)} style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Edit</button>}
                        {isAdmin && <button onClick={() => openTerminate(emp)} style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', border: 'none', background: '#e74c3c', color: '#fff', cursor: 'pointer' }}>Terminate</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {activeEmployees.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: '#999' }}>No active employees yet.</td></tr>
              )}
            </tbody>
          </table>
        )}

        {activeTab === 'terminated' && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
                <th style={{ padding: '15px' }}>Name</th>
                <th>Position</th>
                <th>Monthly Salary</th>
                <th>Termination Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {terminatedEmployees.map((emp) => (
                <tr key={emp.id} style={{ borderBottom: '1px solid #f9f9f9', color: '#777' }}>
                  <td style={{ padding: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Avatar url={emp.photoUrl} />
                      <div>
                        <b>{emp.name}</b>
                        <div style={{ fontSize: '11px', color: '#999' }}>{emp.phone}</div>
                      </div>
                    </div>
                  </td>
                  <td>{emp.position || '-'}</td>
                  <td>৳ {formatTk(emp.monthlySalary)}</td>
                  <td>
                    {emp.terminationDate || '-'}
                    {emp.terminationReason && <div style={{ fontSize: '11px', color: '#999' }}>"{emp.terminationReason}"</div>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {isAdmin && <button onClick={() => openReactivate(emp)} style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Reactivate</button>}
                      {isAdmin && <button onClick={() => removeEmployee(emp)} style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', border: 'none', background: '#e74c3c', color: '#fff', cursor: 'pointer' }}>Delete</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {terminatedEmployees.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: '#999' }}>No terminated employees.</td></tr>
              )}
            </tbody>
          </table>
        )}
    </>
  );

  const modals = (
    <>
      {showEmployeeForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '30px', borderRadius: '20px', width: '400px', maxWidth: '95%', position: 'relative', maxHeight: '90vh', overflowY: 'auto' }}>
            <button onClick={() => setShowEmployeeForm(false)} style={{ position: 'absolute', top: '15px', right: '15px', border: 'none', background: '#ffeded', color: 'red', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer' }}><X size={16} /></button>
            <h3 style={{ marginTop: 0 }}>{editingEmployee ? 'Edit Employee' : 'New Employee'}</h3>

            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
              <Avatar url={employeeForm.photoUrl} />
              <label style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #ddd', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', color: '#4a00e0' }}>
                {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                <input type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} disabled={uploadingPhoto} />
              </label>
            </div>

            <input className="input-field" placeholder="Name *" value={employeeForm.name} onChange={(e) => setEmployeeForm({ ...employeeForm, name: e.target.value })} />
            <input className="input-field" placeholder="Phone Number" value={employeeForm.phone} onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })} />
            <input className="input-field" placeholder="Position (e.g. Salesman)" value={employeeForm.position} onChange={(e) => setEmployeeForm({ ...employeeForm, position: e.target.value })} />
            <input className="input-field" placeholder="Department (e.g. Sales, Delivery)" value={employeeForm.department} onChange={(e) => setEmployeeForm({ ...employeeForm, department: e.target.value })} />
            <select className="input-field" value={employeeForm.employmentType} onChange={(e) => setEmployeeForm({ ...employeeForm, employmentType: e.target.value })}>
              <option value="Full-Time">Full-Time</option>
              <option value="Part-Time">Part-Time</option>
            </select>
            <select className="input-field" value={employeeForm.gender} onChange={(e) => setEmployeeForm({ ...employeeForm, gender: e.target.value })}>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
            <input className="input-field" type="number" placeholder="Monthly Salary (৳)" value={employeeForm.monthlySalary} onChange={(e) => setEmployeeForm({ ...employeeForm, monthlySalary: e.target.value })} />

            <label style={{ fontSize: '12px', color: '#888', fontWeight: 'bold' }}>Joining Date</label>
            <input className="input-field" type="date" value={employeeForm.joiningDate} onChange={(e) => setEmployeeForm({ ...employeeForm, joiningDate: e.target.value })} />

            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px dashed #ccc' }}>
              <b style={{ fontSize: '13px', color: '#666' }}>Payment Details (optional)</b>
              <p style={{ fontSize: '11px', color: '#999', margin: '4px 0 10px' }}>Fill in whichever this employee has -- Pay Salary will then suggest these automatically.</p>
              <input className="input-field" placeholder="bKash Number" value={employeeForm.bkashNumber} onChange={(e) => setEmployeeForm({ ...employeeForm, bkashNumber: e.target.value })} />
              <input className="input-field" placeholder="Bank Account Number" value={employeeForm.bankAccountNumber} onChange={(e) => setEmployeeForm({ ...employeeForm, bankAccountNumber: e.target.value })} />
              <input className="input-field" placeholder="Account Name" value={employeeForm.bankAccountName} onChange={(e) => setEmployeeForm({ ...employeeForm, bankAccountName: e.target.value })} />
              <input className="input-field" placeholder="Branch Name (with code)" value={employeeForm.bankBranchName} onChange={(e) => setEmployeeForm({ ...employeeForm, bankBranchName: e.target.value })} />
              <input className="input-field" placeholder="Bank Name" value={employeeForm.bankName} onChange={(e) => setEmployeeForm({ ...employeeForm, bankName: e.target.value })} />
              <input className="input-field" placeholder="Routing Number" value={employeeForm.bankRoutingNumber} onChange={(e) => setEmployeeForm({ ...employeeForm, bankRoutingNumber: e.target.value })} />
            </div>

            <button onClick={saveEmployee} className="btn-primary" style={{ width: '100%', padding: '12px', marginTop: '5px' }}>Save</button>
          </div>
        </div>
      )}

      {terminatingEmployee && (
        <div onClick={() => setTerminatingEmployee(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '20px', padding: '25px', width: '380px', maxWidth: '95%' }}>
            <h3 style={{ marginTop: 0, color: '#c0392b' }}>⚠️ Terminate Employee</h3>
            <p style={{ fontSize: '13px', color: '#666' }}>
              Are you sure you want to terminate <b>{terminatingEmployee.name}</b>? They will be moved to the Terminated Employees list and no longer counted in active payroll totals.
            </p>
            <label style={{ fontSize: '12px', color: '#888', fontWeight: 'bold' }}>Termination Date</label>
            <input className="input-field" type="date" value={terminationDate} onChange={(e) => setTerminationDate(e.target.value)} />
            <label style={{ fontSize: '12px', color: '#888', fontWeight: 'bold' }}>Reason (required)</label>
            <input className="input-field" placeholder="Why is this employee being terminated?" value={terminationReason} onChange={(e) => setTerminationReason(e.target.value)} />
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button onClick={() => setTerminatingEmployee(null)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={confirmTerminate} disabled={!terminationReason.trim()} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#e74c3c', color: '#fff', fontWeight: 'bold', cursor: terminationReason.trim() ? 'pointer' : 'not-allowed', opacity: terminationReason.trim() ? 1 : 0.5 }}>Confirm Termination</button>
            </div>
          </div>
        </div>
      )}

      {reactivatingEmployee && (
        <div onClick={() => setReactivatingEmployee(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '20px', padding: '25px', width: '380px', maxWidth: '95%' }}>
            <h3 style={{ marginTop: 0, color: '#218c61' }}>Reactivate Employee</h3>
            <p style={{ fontSize: '13px', color: '#666' }}>
              Reactivate <b>{reactivatingEmployee.name}</b>? They will move back to Active Employees.
            </p>
            <label style={{ fontSize: '12px', color: '#888', fontWeight: 'bold' }}>Reason (required)</label>
            <input className="input-field" placeholder="Why is this employee being reactivated?" value={reactivationReason} onChange={(e) => setReactivationReason(e.target.value)} />
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
              <button onClick={() => setReactivatingEmployee(null)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Cancel</button>
              <button onClick={confirmReactivate} disabled={!reactivationReason.trim()} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#10b981', color: '#fff', fontWeight: 'bold', cursor: reactivationReason.trim() ? 'pointer' : 'not-allowed', opacity: reactivationReason.trim() ? 1 : 0.5 }}>Confirm Reactivation</button>
            </div>
          </div>
        </div>
      )}

      {actionEmployee && (
        <div onClick={closeAction} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '30px', borderRadius: '20px', width: '380px', maxWidth: '95%', position: 'relative' }}>
            <button onClick={closeAction} style={{ position: 'absolute', top: '15px', right: '15px', border: 'none', background: '#ffeded', color: 'red', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer' }}><X size={16} /></button>
            <h3 style={{ marginTop: 0 }}>{actionTitle} — {actionEmployee.name}</h3>
            {actionType === 'salary' && (
              <>
                <label style={{ fontSize: '12px', color: '#888', fontWeight: 'bold' }}>For Month</label>
                <input className="input-field" type="month" value={actionMonth} onChange={(e) => setActionMonth(e.target.value)} />
              </>
            )}
            <input className="input-field" type="number" placeholder="Amount (৳)" value={actionAmount} onFocus={(e) => e.target.select()} onChange={(e) => setActionAmount(e.target.value)} />

            <label style={{ fontSize: '12px', color: '#888', fontWeight: 'bold' }}>Paid Via</label>
            <select className="input-field" value={actionPaymentMethod} onChange={(e) => setActionPaymentMethod(e.target.value)}>
              <option value="Cash">Cash</option>
              <option value="Bank">Bank</option>
              <option value="bKash">bKash</option>
            </select>
            {actionPaymentMethod === 'Bank' && (
              actionEmployee.bankAccountNumber ? (
                <p style={{ fontSize: '12px', color: '#666', background: '#f8f9ff', padding: '8px 10px', borderRadius: '8px', marginTop: '-6px' }}>
                  {actionEmployee.bankName || 'Bank'} — {actionEmployee.bankAccountName || actionEmployee.name} — A/C: {actionEmployee.bankAccountNumber}
                  {actionEmployee.bankBranchName ? `, ${actionEmployee.bankBranchName}` : ''}
                </p>
              ) : (
                <p style={{ fontSize: '11px', color: '#e74c3c', marginTop: '-6px' }}>No bank details saved for {actionEmployee.name} yet -- add them via Edit Employee.</p>
              )
            )}
            {actionPaymentMethod === 'bKash' && (
              actionEmployee.bkashNumber ? (
                <p style={{ fontSize: '12px', color: '#666', background: '#f8f9ff', padding: '8px 10px', borderRadius: '8px', marginTop: '-6px' }}>bKash: {actionEmployee.bkashNumber}</p>
              ) : (
                <p style={{ fontSize: '11px', color: '#e74c3c', marginTop: '-6px' }}>No bKash number saved for {actionEmployee.name} yet -- add it via Edit Employee.</p>
              )
            )}

            <input className="input-field" placeholder="Note (optional)" value={actionNote} onChange={(e) => setActionNote(e.target.value)} />
            <button onClick={submitAction} className="btn-confirm" style={{ width: '100%', padding: '12px', marginTop: '5px' }}>Confirm</button>

            {historyForEmployee(actionEmployee.id).length > 0 && (
              <div style={{ marginTop: '18px', paddingTop: '12px', borderTop: '1px dashed #ccc' }}>
                <b style={{ fontSize: '12px', color: '#888' }}>Recent Transactions</b>
                {historyForEmployee(actionEmployee.id).map((t) => (
                  <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#555', marginTop: '5px' }}>
                    <span>{formatDateTime(t.createdAt)} — {t.type === 'salary' ? `Salary (${t.month || '-'})` : (t.amount < 0 ? 'Advance Repaid' : 'Advance')}{t.paymentMethod ? ` via ${t.paymentMethod}` : ''}</span>
                    <span style={{ fontWeight: 'bold', color: t.amount < 0 ? '#10b981' : '#333' }}>৳ {formatTk(Math.abs(t.amount))}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );

  if (embedded) {
    return (
      <>
        {boxContent}
        {modals}
      </>
    );
  }

  return (
    <div style={{ padding: '20px', background: '#f4f7fe', minHeight: '100vh' }}>
      <div className="white-box" style={{ padding: '30px', borderRadius: '25px', background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
        {boxContent}
      </div>
      {modals}
    </div>
  );
};

export default Payroll;
